<script setup lang="ts">
import dayjs from 'dayjs'
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const getLocalizedText = (
  value: FeedActivity['title'] | FeedActivity['description'] | string | null | undefined
): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[locale.value as keyof typeof value] || value.en || ''
  }
  return ''
}

const description = computed(() => {
  const descText = getLocalizedText(props.activity.description)
  if (descText) return descText

  const points = props.activity.data?.points || 0
  const message = props.activity.data?.autopilot_name || props.activity.data?.message || ''
  const translationKey =
    points === 0
      ? 'pages.customer.customerDetailPage.blocks.activity.autopilot.withoutPointText'
      : 'pages.customer.customerDetailPage.blocks.activity.autopilot.withPointText'

  return t(translationKey, { points, message })
})

const iconColor = computed(() => {
  return 'bg-yellow-400'
})

const detailTitle = computed(() => {
  return t('pages.customer.customerDetailPage.blocks.activity.autopilot.detail.title')
})

const detailText = computed(() => {
  return props.activity.data?.autopilot_name || props.activity.data?.message || ''
})

const isConverted = computed(() => {
  return Boolean(props.activity.data?.converted_at)
})

const conversionDateText = computed(() => {
  if (!isConverted.value || !props.activity.data?.converted_at) return ''

  const convertedDate = dayjs(props.activity.data.converted_at).locale(locale.value)
  return t('pages.customer.customerDetailPage.blocks.activity.autopilot.detail.conversionDate', {
    date: convertedDate.format('DD/MM/YYYY'),
    time: convertedDate.format('HH:mm')
  })
})
</script>

<template>
  <ActivityCardBase
    :icon="activity.icon || 'HeroBoltIconSolid'"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #description>
      <p class="flex text-sm text-muted-foreground">
        <Popover v-if="detailText">
          <PopoverTrigger class="border-b border-dashed text-left focus:outline-hidden">
            {{ description }}
          </PopoverTrigger>
          <PopoverContent class="w-80 overflow-hidden border p-0 shadow-lg">
            <div class="bg-muted p-4">
              <span class="flex items-center">
                <span class="text-sm font-medium text-foreground">{{ detailTitle }}</span>
              </span>
              <span v-if="detailText" class="mt-1 block text-sm text-muted-foreground">
                {{ detailText }}
              </span>
            </div>
            <span v-if="isConverted && conversionDateText" class="block px-4 py-2 text-sm text-muted-foreground">
              {{ conversionDateText }}
            </span>
          </PopoverContent>
        </Popover>
        <span v-else>{{ description }}</span>
      </p>
    </template>
  </ActivityCardBase>
</template>
